/**
 * ConversionFunnelChart — Stepped bar chart showing conversion stages.
 *
 * Renders 4 full-width pill bars with decreasing opacity per stage.
 * Labels are rendered inside bars for steps 0–2. Step 3 (last, lightest bar)
 * renders its label BELOW the bar in #7C3AED for WCAG AA contrast safety.
 *
 * Bar opacities:
 *   Step 0: rgba(139,92,246,1.00)
 *   Step 1: rgba(139,92,246,0.75)
 *   Step 2: rgba(139,92,246,0.55)
 *   Step 3: rgba(139,92,246,0.40) → label below in #7C3AED
 *
 * Wrapped in GlassCard withChromeBorder={false}.
 */

import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ViewStyle,
  TextStyle,
} from 'react-native';
import { GlassCard } from '../../glass/GlassCard';

// ---------------------------------------------------------------------------
// Module-level constants
// ---------------------------------------------------------------------------

const FUNNEL_PURPLE = 'rgba(139,92,246,';
const BAR_OPACITIES = [1.0, 0.75, 0.55, 0.40] as const;
const LAST_BAR_LABEL_COLOR = '#7C3AED';
const BAR_HEIGHT = 44;
const BAR_GAP = 10;

// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

export interface FunnelStep {
  label: string;
  count: number;
  conversionRate: number | null;
  accessibilityLabel: string;
}

interface ConversionFunnelChartProps {
  steps: FunnelStep[];
  title: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ConversionFunnelChart: React.FC<ConversionFunnelChartProps> = ({
  steps,
  title,
}) => {
  const lastIndex = steps.length - 1;

  return (
    <GlassCard withChromeBorder={false}>
      <Text style={styles.sectionTitle}>{title}</Text>

      <View accessibilityRole="summary" style={styles.chartContainer}>
        {steps.map((step, index) => {
          const opacity = BAR_OPACITIES[index] ?? 0.40;
          const barColor = `${FUNNEL_PURPLE}${opacity})`;
          const isLast = index === lastIndex;

          const labelText =
            step.conversionRate != null
              ? `${step.count} ${step.label} (${step.conversionRate}%)`
              : `${step.count} ${step.label}`;

          return (
            <View
              key={step.label}
              style={[styles.barWrapper, index < lastIndex && { marginBottom: BAR_GAP }]}
            >
              {/* Bar */}
              <View
                style={[styles.bar, { backgroundColor: barColor }]}
                accessible={true}
                accessibilityLabel={step.accessibilityLabel}
              >
                {/* Inside label for steps 0–2 */}
                {!isLast && (
                  <Text style={styles.insideLabel} numberOfLines={1}>
                    {labelText}
                  </Text>
                )}
              </View>

              {/* Below label for step 3 (light bar — contrast safety) */}
              {isLast && (
                <Text style={styles.belowLabel}>{labelText}</Text>
              )}
            </View>
          );
        })}
      </View>
    </GlassCard>
  );
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create({
  sectionTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: '#18181B',
    marginBottom: 16,
  } as TextStyle,
  chartContainer: {
    // No additional padding — GlassCard provides 16px
  } as ViewStyle,
  barWrapper: {
    // Each bar row + optional below label
  } as ViewStyle,
  bar: {
    height: BAR_HEIGHT,
    borderRadius: 8,
    justifyContent: 'center',
  } as ViewStyle,
  insideLabel: {
    fontSize: 13,
    fontWeight: '700',
    color: '#FFFFFF',
    paddingHorizontal: 14,
  } as TextStyle,
  belowLabel: {
    fontSize: 13,
    fontWeight: '700',
    color: LAST_BAR_LABEL_COLOR,
    marginTop: 6,
    paddingHorizontal: 4,
  } as TextStyle,
});
